import EntityPreferenceSession from './components/EntityPreferenceSession';
import 'animate.css';
import './firebaseui-styling.global.css';

import AppThemeProvider from './components/AppThemeProvider';

// Redux.
import { Provider } from 'react-redux';
import store from './redux/store';
import { installAuthSessionRecovery } from './utils/restoreAuthSession';
import { installPlaybackSync } from './utils/episodePlayback';
import { installAudioProgressRecorder } from './utils/audioProgressRecorder';

// Routes.
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import CuratePodcastsForYou from "./routes/curate_podcasts_for_you";
import Chat from "./routes/chat";
import Chats from "./routes/chats";
import DebugChatV2Run from './routes/debug_chatv2_run';
import Explore from "./routes/explore";
import DownloadAndroid from './routes/download_android';
import DownloadWebApp from './routes/download_web_app';
import EpisodeHighlights from "./routes/episode_highlights";
import Home from "./routes/home";
import ImportShows from "./routes/import_shows";
import Library from "./routes/library";
import LoadPodcast from "./routes/load_podcast";
import Signin from "./routes/signin";
import SigninNewUser from "./routes/signin_new_user";
import Missing from "./routes/missing";
import MyNotes from "./routes/my_notes";
import Notes from "./routes/notes";
import Podcast from './routes/podcast';
import Preview from './routes/preview';
import Previews from './routes/previews';
import QRCode from './routes/qrcode';
import QRCodeScan from './routes/qrcode_scan';
import Played from './routes/played';
import Saved from './routes/saved';
import SavedHighlightDrawer from './components/SavedHighlightDrawer';
import Search from "./routes/search";
import Speaker from "./routes/speaker";
import Support from "./routes/support";
import ChatThread, { PublicChatThread } from './routes/chat_thread';
import ChatThreads from './routes/chat_threads';
import Topic from "./routes/topic";
import Track from "./routes/track";
import Video from './routes/video';
import Settings from './routes/settings';
import PlaybackSettings from './routes/playback_settings';
import {
  ConnectedSettingsTopics as SettingsTopics,
  ConnectedSettingsSpeakers as SettingsSpeakers,
  ConnectedSettingsShows as SettingsShows,
} from './routes/settings_preferences';
import MyTest from './routes/my_test';
import Menu from './routes/menu';
import { useEffect, useLayoutEffect } from 'react';
import AskSessionProvider from './components/AskSession';
import { getAskBackgroundLocation, RouteScrollRestoration, useHomeAskPrefill } from './utils/askNavigation';
import './utils/askNavigation.css';
import { buildMissingRoute } from './utils/missingRoute';
import './appViewport.css';
import AppViewport from './components/AppViewport';
import { getVideoBackgroundLocation, isVideoRoute } from './utils/videoNavigation';

function MissingRedirect() {
  const location = useLocation();

  return <Navigate to={buildMissingRoute(location)} replace />;
}

function GetStartedRedirect() {
  const location = useLocation();

  return <Navigate to='/#explore' state={location.state} replace />;
}


function HomeEntry() {
  const hasPrefill = useHomeAskPrefill();
  return hasPrefill ? null : <Home />;
}

function AppRoutes() {
  const location = useLocation();
  const videoBackgroundLocation = getVideoBackgroundLocation(location);
  const backgroundLocation = videoBackgroundLocation || getAskBackgroundLocation(location);
  const hasBackground = Boolean(backgroundLocation);
  const isAsk = location.pathname === '/ask';
  const isVideo = isVideoRoute(location.pathname);

  useLayoutEffect(() => {
    window.updatePodeuxSmartAppBanner?.();
  }, [location.pathname, location.search, location.hash]);

  useLayoutEffect(() => {
    if (!isAsk) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousAskOpen = document.body.getAttribute('data-ask-open');
    document.body.style.overflow = 'hidden';
    document.body.setAttribute('data-ask-open', 'true');
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousAskOpen === null) document.body.removeAttribute('data-ask-open');
      else document.body.setAttribute('data-ask-open', previousAskOpen);
    };
  }, [isAsk]);

  useLayoutEffect(() => {
    if (!isVideo) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.setAttribute('data-video-open', 'true');
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.removeAttribute('data-video-open');
    };
  }, [isVideo]);

  return (
    <>
      <RouteScrollRestoration suspended={isVideo} />
      <SavedHighlightDrawer />
      <EntityPreferenceSession />
      <div
        className="appRouteBackground"
        hidden={hasBackground}
        inert={backgroundLocation ? '' : undefined}
        aria-hidden={backgroundLocation ? 'true' : undefined}
      >
          <Routes location={backgroundLocation || location}>
            <Route path="/" element={<HomeEntry />} />
            <Route path="/ask" element={<Home focused />} />
            <Route path="/chat">
              <Route path=":chatId" element={<Chat />}>
              </Route>
            </Route>
            <Route path="/chat_thread">
              <Route path=":threadId" element={<ChatThread />}>
              </Route>
            </Route>
            <Route path="/debug_chatv2_run">
              <Route path=":threadId/:runId" element={<DebugChatV2Run />}>
              </Route>
            </Route>
            <Route path="/public_chat_thread">
              <Route path=":sharedThreadId" element={<PublicChatThread />}> 
              </Route>
            </Route>
            <Route path="/chat_v3_thread/:threadId" element={<ChatThread engineVersion={3} />} />
            <Route path="/public_chat_v3_thread/:sharedThreadId" element={<PublicChatThread engineVersion={3} />} />
            <Route path="/chat_threads" element={<ChatThreads />}></Route>
            <Route path="/chats" element={<Chats />}></Route>
            <Route path="/curate_podcasts_for_you" element={<CuratePodcastsForYou />}></Route>
            <Route path="/explore" element={<Explore />}></Route>
            {/* <Route path="/" element={<Down />} /> */}
            <Route path="/download_android" element={<DownloadAndroid />}></Route>
            <Route path="/download_webapp" element={<DownloadWebApp />}></Route>
            <Route path="/episode_highlights">
              <Route path=":trackId" element={<EpisodeHighlights />}>
              </Route>
            </Route>
            <Route path="/get_started" element={<GetStartedRedirect />}></Route>
            <Route path="/home" element={<HomeEntry />}></Route>
            <Route path="/homev2" element={<Navigate to="/home" replace />}></Route>
            <Route path="/homie" element={<Navigate to="/home" replace />}></Route>
            <Route path="/import_shows" element={<ImportShows />}></Route>
            <Route path="/library" element={<Library />}></Route>
            <Route path="/load_podcast" element={<LoadPodcast />}></Route>
            <Route path="/sign_in" element={<Signin />}></Route>
            <Route path="/signin" element={<Signin />}></Route>
            <Route path="/signin_new_user" element={<SigninNewUser />}></Route>
            <Route path="/menu" element={<Menu />}></Route>
            <Route path="/missing" element={<Missing />}></Route>
            <Route path="/my_notes" element={<MyNotes />}></Route>
            <Route path="/notes" element={<MyNotes />}></Route>
            <Route path="/trending_notes" element={<Notes />}></Route>
            <Route path="/podcast">
              <Route index element={<MissingRedirect />}></Route>
              <Route path=":podcastId" element={<Podcast />}>
              </Route>
            </Route>
            <Route path="/preview">
              <Route index element={<MissingRedirect />}></Route>
              <Route path=":trackId">
                <Route index element={<MissingRedirect />}></Route>
                <Route path=":messageIndex" element={<Preview />}>
                </Route>
              </Route>
            </Route>
            <Route path="/previews" element={<Previews />}></Route>
            <Route path="/qr_code" element={<QRCode />}></Route>
            <Route path="/qr_code_scan" element={<QRCodeScan />}></Route>
            <Route path="/played" element={<Played />}></Route>
            <Route path="/saved" element={<Saved />}></Route>
            <Route path="/search" element={<Search />}>
              <Route path=":query" element={<Search />}>
              </Route>
            </Route>
            <Route path="/settings" element={<Settings />}></Route>
            <Route path="/settings/playback" element={<PlaybackSettings />}></Route>
            <Route path="/settings/topics" element={<SettingsTopics />}></Route>
            <Route path="/settings/speakers" element={<SettingsSpeakers />}></Route>
            <Route path="/settings/shows" element={<SettingsShows />}></Route>
            <Route path="/speaker">
              <Route index element={<MissingRedirect />}></Route>
              <Route path=":speakerId" element={<Speaker />}>
              </Route>
            </Route>
            <Route path="/support" element={<Support />}></Route>
            <Route path="/topic">
              <Route path=":topicId" element={<Topic />}>
              </Route>
            </Route>
            <Route path="/track">
              <Route path=":trackId/video/:videoId" element={<Video />} />
              <Route path=":trackId" element={<Track />}>
              </Route>
            </Route>
            <Route path="/my_test" element={<MyTest />}></Route>
            <Route path="*" element={<MissingRedirect />}></Route>
          </Routes>
      </div>
      {backgroundLocation && isAsk && (
        <div className="appAskOverlay">
          <Routes>
            <Route path="/ask" element={<Home focused />} />
          </Routes>
        </div>
      )}
      {videoBackgroundLocation && (
        <Routes>
          <Route path='/track/:trackId/video/:videoId' element={<Video />} />
        </Routes>
      )}
    </>
  );
}


function App() {
  useLayoutEffect(() => installAudioProgressRecorder(store), []);
  useEffect(() => installAuthSessionRecovery(store.dispatch, store.getState), []);
  useEffect(() => installPlaybackSync(store), []);
  return (
    <Provider store={store}>
      <AppThemeProvider>
        <BrowserRouter>
          <AskSessionProvider>
            <AppViewport>
              <AppRoutes />
            </AppViewport>
          </AskSessionProvider>
        </BrowserRouter>
      </AppThemeProvider>
    </Provider>
  );
}

export default App;
